<template>
  <BaseLayout>
    <BaseDashboard :is-loading="isPageLoading">
      <div
        v-if="hasTemplate"
        id="page-contaienr"
        class="overflow-hidden d-flex flex-column"
      >
        <div class="flex-grow-1 overflow-y-auto p-5">
          <b-form-group
            label-class="p-0 mb-0"
            label-for="template-name"
            :state="fieldState('templateForm', 'name')"
            invalid-feedback="Please provide a name"
          >
            <template
              slot="label"
            >
              Name <span class="form-required">*</span>
            </template>
            <p
              class="form-text text-muted m-0 pb-1"
            >
              <small>
                Please use a descriptive name to make it easier to find.
                (Advertiser YYYY QX - Creative) e.g. AB 2018 Q1 - Editorial Billboard
              </small>
            </p>
            <b-form-input
              id="template-name"
              type="text"
              v-model="$v.templateForm.name.$model"
              trim
              :state="fieldState('templateForm','name')"
              data-test="template-name"
            />
          </b-form-group>
          <b-row
            class="gutters-0 new-gutters-lg"
          >
            <b-col
              cols="6"
            >
              <b-form-group
                label-for="template-type"
                :state="fieldState('templateForm','typeId')"
                invalid-feedback="Please provide a type"
              >
                <template
                  slot="label"
                >
                  Type <span class="form-required">*</span>
                </template>
                <b-form-select
                  id="template-type"
                  :options="creativeTypes"
                  v-model="$v.templateForm.typeId.$model"
                  @input="onTypeChange"
                  :state="fieldState('templateForm','typeId')"
                  :disabled="isLive"
                  data-test="template-type"
                />
                <b-form-checkbox
                  size="sm"
                  class="private-checkbox mt-2"
                  v-model="templateForm.private"
                  :disabled="!isSelfServiceType()"
                  data-test="private-checkbox"
                >
                  Private Template
                </b-form-checkbox>
              </b-form-group>
            </b-col>
            <b-col
              cols="6"
            >
              <b-form-group
                label-for="template-size"
                :state="fieldState('templateForm','sizeId')"
                invalid-feedback="Please provide a size"
              >
                <template
                  slot="label"
                >
                  Size <span class="form-required">*</span>
                </template>
                <b-form-select
                  id="template-size"
                  :options="creativeSizes"
                  v-model="$v.templateForm.sizeId.$model"
                  :state="fieldState('templateForm','sizeId')"
                  :disabled="isLive"
                  data-test="template-size"
                />
              </b-form-group>
            </b-col>
          </b-row>
          <b-form-group
            label-for="template-group"
          >
            <template
              slot="label"
            >
              Groups
            </template>
            <TagInput
              :options="suggestedGroupOptions"
              :tags="assignedGroups"
              @change="onUpdateTemplateGroups"
            />
          </b-form-group>
          <b-row
            v-if="!isNativeType()"
            id="template-editor-container"
            class="gutters-0 new-gutters-sm overflow-hidden"
          >
            <b-col
              class="col-8"
            >
              <b-form-group
                class="m-0"
                label="Template"
                label-for="template-template"
                :state="fieldState('templateForm','content')"
                invalid-feedback="Please enter a value for template"
              >
                <BaseEditor
                  v-model="$v.templateForm.content.$model"
                  data-test="content-input"
                />
              </b-form-group>
            </b-col>
            <b-col
              class="col-4 h-100 overflow-hidden d-flex flex-column"
            >
              <h5 class="m-0 mb-3">
                Common macros
              </h5>
              <div
                class="flex-grow-1 overflow-hidden"
              >
                <b-card
                  no-body
                  class="m-0 p-0 h-100"
                >
                  <b-card-body
                    class="overflow-y-auto"
                  >
                    <b-list-group class="list-group-flush">
                      <b-list-group-item>
                        <code class="u-selectable">${headline}</code>
                        <small class="text-muted ml-4">- article headline</small>
                      </b-list-group-item>
                      <b-list-group-item>
                        <code class="u-selectable">${displayText}</code>
                        <small class="text-muted ml-4">- article display content</small>
                      </b-list-group-item>
                      <b-list-group-item>
                        <code class="u-selectable text-info">${subHeadline}</code>
                        <small class="text-muted ml-4">- (if set) article sub headline variable</small>
                      </b-list-group-item>
                      <b-list-group-item>
                        <code class="u-selectable text-info">${imageFileName}</code>
                        <small class="text-muted ml-4">- (if set) Iamge file name or url variable</small>
                      </b-list-group-item>
                      <b-list-group-item>
                        <code class="u-selectable text-info">${alternateCTA}</code>
                        <small class="text-muted ml-4">- (if set) alternate call to action variable</small>
                      </b-list-group-item>
                      <b-list-group-item>
                        <code class="u-selectable text-info">${alternateScrollBox}</code>
                        <small class="text-muted ml-4">- (if set) alternate call to action variable</small>
                      </b-list-group-item>
                      <b-list-group-item>
                        <code class="u-selectable text-info">${disclaimer}</code>
                        <small class="text-muted ml-4">- (if set) alternate scroll box variable</small>
                      </b-list-group-item>
                    </b-list-group>
                    <div>
                      <h5 class="mt-3">
                        Scroll tracker
                      </h5>
                      <b-list-group class="list-group-flush">
                        <code class="u-selectable text-info">
                          &lt;script type="text/javascript"&gt;
                          var advertiserContentId = "${advertiserContentId}"
                          &lt;/script&gt;
                          <br><br>
                          &lt;script type="application/javascript" src="//s.creative.cdn.smartology.co/scroll-tracker.js"&gt;&lt;/script&gt;
                          <br><br>
                          &lt;#include "ftl-includes/tracking/eventLibraryLoadingMacro.ftl"&gt;
                          &lt;@EventLibraryLoader eventCategoriesJSArrayAsString="['scrollProgress']" /&gt;</code>
                      </b-list-group>
                    </div>
                  </b-card-body>
                </b-card>
              </div>
            </b-col>
          </b-row>
        </div>
        <base-wizard-footer
          id="fixed-footer"
          :next-text="nextButtonText"
          :is-loading="isSubmitting"
          :show-back-button="true"
          @previous-step="goBack"
          @next-step="onSubmit"
          :last-disabled="isSubmitting"
          :next-disabled="!isValid('templateForm') || isSubmitting"
          data-test="template-form"
        />
      </div>
      <b-card
        v-if="!hasTemplate"
        class="text-center"
      >
        <i class="fa-light fa-exclamation-triangle display-1 text-muted mb-5" />
        <p class="h4 text-muted font-weight-normal mb-7">
          The template you are looking for doesn't exist.
        </p>
        <b-button
          size="lg"
          :to="{ name: 'Templates'}"
        >
          Go to template gallery
          <i class="fa-light fa-arrow-right mr-2" />
        </b-button>
      </b-card>
    </BaseDashboard>
  </BaseLayout>
</template>

<script>
import { mapActions } from 'vuex';
import * as templatesTypes from '@/modules/templates/store/types';
import * as campaignsTypes from '@/modules/campaigns/store/types';
import { required, requiredIf } from 'vuelidate/lib/validators';
import validationMixin from '@/core/mixins/validation';
import TagInput from '@/modules/templates/components/TagInput';

export default {
  name: 'TemplateForm',
  mixins: [
    validationMixin,
  ],
  components: {
    TagInput,
  },
  data() {
    return {
      isPageLoading: false,
      isSubmitting: false,
      hasTemplate: true,
      creativeSizes: [],
      creativeTypes: [],
      availableGroups: [],
      assignedGroups: [],
      templateForm: {
        id: 0,
        name: '',
        typeId: '',
        sizeId: '',
        content: '',
        private: false,
        live: false,
      },
    };
  },
  validations: {
    templateForm: {
      name: {
        required,
      },
      sizeId: {
        required,
      },
      typeId: {
        required,
      },
      group: {},
      content: {
        // eslint-disable-next-line func-names
        required: requiredIf(function () {
          return !this.isNativeType();
        }),
      },
    },
  },
  computed: {
    isEdit() {
      const { id } = this.$route.params;
      return !!id;
    },
    isLive() {
      return this.templateForm.live;
    },
    nextButtonText() {
      return this.isEdit ? 'Update' : 'Create';
    },
    suggestedGroupOptions() {
      if (this.availableGroups.length === 0) return [];
      return this.availableGroups.map((item) => item.name);
    },
  },
  methods: {
    ...mapActions('campaigns', [
      campaignsTypes.FETCH_CREATIVE_SIZES,
    ]),
    ...mapActions('templates', [
      templatesTypes.FETCH_TEMPLATE,
      templatesTypes.UPDATE_TEMPLATE,
      templatesTypes.FETCH_CREATIVE_TYPES,
      templatesTypes.FETCH_TEMPLATE_GROUPS,
      templatesTypes.CREATE_NEW_TEMPLATE_GROUP,
    ]),
    async onSubmit() {
      this.isSubmitting = true;
      let shouldContinue = true;
      // check if group has been added, if not then show warning popup
      let groupIds = this.assignedGroups
        .map((element) => this.getGroupId(element));
      if (groupIds.length === 0) {
        await this.warnUserNoGroupSet()
          .catch(() => {
            shouldContinue = false;
          });
      }
      if (!shouldContinue) {
        this.isSubmitting = false;
        return;
      }
      const groupMap = groupIds
        .map((group, index) => ({
          id: group,
          name: this.assignedGroups[index],
        }));
      if (groupIds.includes(-1)) {
        const newGroups = groupMap.filter((group) => (group.id === -1));
        const groupPromises = newGroups
          .map((group) => this.CREATE_NEW_TEMPLATE_GROUP(group.name));

        await Promise.all(groupPromises)
          .then((groups) => {
            groupIds = groupMap
              .map((group) => {
                const newId = groups.find((newGroup) => newGroup.name === group.name);
                return newId ? newId.id : group.id;
              });
          });
      }
      this.templateForm.groupIds = groupIds || null;
      this.UPDATE_TEMPLATE(this.templateForm)
        .then((data) => {
          if (!this.isEdit) {
            this.$router.push({ name: 'TemplateUpdate', params: { id: data.id } });
          }
          this.templateForm = data;
          this.setTemplateType();
        })
        .finally(() => {
          this.isSubmitting = false;
        });
    },
    warnUserNoGroupSet() {
      return new Promise((resolve, reject) => {
        this.$bvModal.msgBoxConfirm('You have not added this template to any groups! \n"Yes" to continue, \n"No" to cancel.', {
          title: 'Warning',
          size: 'sm',
          buttonSize: 'sm',
          okVariant: 'danger',
          okTitle: 'Yes',
          cancelTitle: 'No',
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: true,
        })
          .then((value) => {
            if (!value) return reject();
            return resolve();
          })
          .catch(() => reject());
      });
    },
    onTypeChange() {
      this.setTemplateType();
      if (this.isSelfServiceType()) {
        this.templateForm.private = true;
      } else {
        this.templateForm.private = false;
      }
    },
    isNativeType() {
      return this.templateForm.type === 'Native';
    },
    isSelfServiceType() {
      return this.templateForm.type && this.templateForm.type.startsWith('Self Service');
    },
    getGroupId(name) {
      const tempGroup = this.availableGroups
        .find((group) => group.name.toLowerCase() === name.toLowerCase());
      return ((tempGroup === null || tempGroup === undefined) ? -1 : tempGroup.id);
    },
    getGroupName(id) {
      const tempGroup = this.availableGroups
        .find((group) => group.id === id);
      return ((tempGroup === null || tempGroup === undefined) ? -1 : tempGroup.name);
    },
    onUpdateTemplateGroups(groups) {
      this.assignedGroups = groups;
    },
    setTemplateGroups() {
      this.assignedGroups = this.templateForm.groupIds
        .map((id) => this.getGroupName(id));
    },
    setTemplateType() {
      if (this.templateForm.typeId) {
        this.templateForm.type = this.creativeTypes.find((item) => (item.value === this.templateForm.typeId))?.text;
      }
    },
    goBack() {
      if (window.history.length > 1) {
        window.history.back();
      } else {
        window.location.replace(window.document.referrer);
      }
    },
  },
  mounted() {
    this.isPageLoading = true;
  },
  async created() {
    await this.FETCH_TEMPLATE_GROUPS()
      .then((groups) => {
        this.availableGroups = groups;
      });
    this.FETCH_CREATIVE_SIZES()
      .then((sizes) => {
        this.creativeSizes = sizes.map((item) => ({
          text: `${item.width}x${item.height}`,
          value: item.id,
        }));
      });
    await this.FETCH_CREATIVE_TYPES()
      .then((types) => {
        this.creativeTypes = types.map((item) => ({
          text: item.name,
          value: item.id,
        }));
      });
    this.isPageLoading = false;
    const { id } = this.$route.params;
    if (this.isEdit) {
      this.isPageLoading = true;
      this.FETCH_TEMPLATE(id)
        .then((template) => {
          this.templateForm = template;
          this.setTemplateGroups();
          this.setTemplateType();
          this.isPageLoading = false;
        })
        .catch(() => {
          this.hasTemplate = false;
        });
    }
  },
};
</script>
<style lang="scss" scoped>
#page-contaienr {
  height: 100%;
  max-height: 100vh;
}
.form-required::before {
  content: '';
}
#template-editor-container {
  height: 524px;
}
[class*='list-group'] {
  padding: 0;
  margin: 0;
  .list-group-item:not(:first-child) {
    margin-top: 0.75rem;
  }
  .list-group-item:not(:last-child) {
    margin-bottom: 0.75rem;
  }
}
</style>
<style lang="scss">
.private-checkbox  {
  .custom-control-input:disabled ~ .custom-control-label {
    cursor: not-allowed;
  }
}
</style>
